import { useKindeBrowserClientContext } from '@/context/kinde-browser-client-context.utils';
import { isTauri } from '@/tauri/utils';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';

const IS_TAURI = isTauri();

type HeidiKindeUser = {
  email: string | null;
  family_name?: string | null;
  given_name?: string | null;
  id?: string | null;
  picture?: string | null;
} | null;

export type HeidiKindeGetTokenOptions = { isForceRefresh?: boolean };

type HeidiKindeAuth = {
  getToken: (
    options?: HeidiKindeGetTokenOptions
  ) => Promise<string | undefined> | string | null;
  getUser: () => HeidiKindeUser;
  isAuthenticated: boolean | null;
  isLoading: boolean | null;
  user?: HeidiKindeUser;
};

const unauthenticatedKindeAuth = {
  getToken: () => null,
  getUser: () => null,
  isAuthenticated: false,
  isLoading: false,
  user: null,
} satisfies HeidiKindeAuth;

export const useKinde = (): HeidiKindeAuth => {
  const reactKinde = useKindeAuth();
  const nextKinde = useKindeBrowserClientContext();

  if (IS_TAURI) {
    return {
      ...reactKinde,
      // Pass options through to the Kinde React SDK so callers (the
      // auth-token cache's Kinde-desktop branch via the bridge atom)
      // can request a forced refresh. The SDK's local PKCE
      // implementation respects { isForceRefresh: true }; previously
      // the wrapper dropped it on the floor.
      //
      // NB: this arrow function gets a fresh reference each render. The
      // one identity-sensitive consumer is `KindeDesktopBridge`, which
      // captures the latest reference via a ref + a mount-only effect to
      // avoid re-setting the bridge atom on every render (see that file
      // for the rationale).
      getToken: (options) => reactKinde.getToken?.(options) ?? null,
      getUser: () => reactKinde.user ?? null,
      user: reactKinde.user ?? null,
    };
  }

  if (nextKinde !== null) {
    return nextKinde;
  }

  return unauthenticatedKindeAuth;
};
